import React, { useEffect, useMemo, useState } from 'react';
import { RiMapPin2Line } from '@remixicon/react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { SearchSelect } from '@/design-system/search-select';
import { toast } from '@/design-system/toaster';
import { Pitstop } from '@/services/api/pitstops';
import {
  AssignShipmentsHubResult,
  Shipment,
  shipmentsAPI,
} from '@/services/api/shipments';

import { HUB_SKIP_REASON, handoverLabel } from '../shipmentHandover';
import { useSetHubOptions } from './useSetHubOptions';

interface AssignHubModalProps {
  /** The shipments to set; the modal is open while this is non-empty. */
  shipments: Shipment[];
  /** Dash hubs (staffed HUB pitstops), already loaded by the list page. */
  hubs: Pitstop[];
  onClose: () => void;
  /** Called after the request, with the server's per-shipment results. */
  onAssigned: (result: AssignShipmentsHubResult) => void;
}

/**
 * Ops' bulk "Set hub" for partner shipments (`POST /v1/shipments/hub:assign`):
 * any Dash hub, or a pitstop of the shipment's own client — whatever the
 * type. The shipment's handoverPoint decides which side the hub/pitstop
 * becomes (Delivery → pickup point, Retrieval → drop-off point). An
 * unresolved (CREATED) one goes to AT_HUB and the partner gets "Shipment siap
 * ditangani"; one already AT_HUB is moved to the new hub (no status change).
 */
const AssignHubModal: React.FC<AssignHubModalProps> = ({
  shipments,
  hubs,
  onClose,
  onAssigned,
}) => {
  const open = shipments.length > 0;
  const [pitstopId, setPitstopId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { options, loading: loadingPitstops } = useSetHubOptions(
    shipments,
    hubs,
    open
  );

  useEffect(() => {
    if (open) setPitstopId('');
  }, [open]);

  const counts = useMemo(() => {
    const retrieval = shipments.filter(
      (s) => handoverLabel(s.anchorRole) === 'Retrieval'
    ).length;
    const atHub = shipments.filter((s) => s.status === 'AT_HUB').length;
    return {
      retrieval,
      delivery: shipments.length - retrieval,
      atHub,
      created: shipments.length - atHub,
    };
  }, [shipments]);

  const close = () => {
    if (!submitting) onClose();
  };

  const submit = async () => {
    if (!pitstopId) return;
    setSubmitting(true);
    try {
      const result = await shipmentsAPI.assignHub({
        shipmentIds: shipments.map((s) => s.id),
        pitstopId: Number(pitstopId),
      });
      const skipped = result.results.filter(
        (r) => r.outcome !== 'assigned' && r.outcome !== 'reassigned'
      );
      if (result.assigned > 0 || result.reassigned > 0) {
        toast.success(`Hub di-set: ${result.pitstop.name}`, {
          description: [
            result.assigned > 0
              ? `${result.assigned} shipment sekarang AT_HUB.`
              : null,
            result.reassigned > 0
              ? `${result.reassigned} shipment dipindah hub-nya.`
              : null,
          ]
            .filter(Boolean)
            .join(' '),
        });
      }
      if (skipped.length > 0) {
        toast.error(`${skipped.length} shipment tidak di-set`, {
          description: skipped
            .map(
              (r) =>
                `${r.waybill ?? r.shipmentId}: ${
                  HUB_SKIP_REASON[r.outcome as keyof typeof HUB_SKIP_REASON]
                }`
            )
            .join('\n'),
        });
      }
      onAssigned(result);
    } catch (err) {
      toast.error('Gagal set hub', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      open={open}
      onClose={close}
      icon={<RiMapPin2Line />}
      title="Set hub"
      subtitle={`${shipments.length} shipment dipilih`}
      size="sm"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="neutral"
            style="stroke"
            onClick={close}
            disabled={submitting}
          >
            Batal
          </Button>
          <Button
            onClick={submit}
            disabled={!pitstopId || submitting}
            loading={submitting}
            data-testid="assign-hub-submit"
          >
            {submitting ? 'Menyimpan…' : 'Set hub'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-text-sub-600">
          Hub/pitstop ini jadi titik pickup untuk shipment{' '}
          <strong>Delivery</strong> ({counts.delivery}) dan titik dropoff untuk
          shipment <strong>Retrieval</strong> ({counts.retrieval}).
          {counts.created > 0 && (
            <>
              {' '}
              Shipment yang belum punya hub ({counts.created}) langsung jadi{' '}
              <strong>AT_HUB</strong> dan client menerima webhook “Shipment siap
              ditangani”.
            </>
          )}
          {counts.atHub > 0 && (
            <>
              {' '}
              Shipment yang sudah <strong>AT_HUB</strong> ({counts.atHub})
              dipindah ke hub ini — status tetap, tercatat di timeline, tanpa
              webhook.
            </>
          )}
        </p>
        <SearchSelect
          placeholder="Cari atau pilih hub / pitstop…"
          options={options}
          value={pitstopId}
          onChange={(next) => setPitstopId(String(next))}
          loading={loadingPitstops}
          disabled={submitting}
          emptyMessage="Tidak ada hub / pitstop"
        />
        <p className="text-[11px] leading-relaxed text-text-sub-600">
          Pitstop client hanya bisa dipakai untuk shipment milik client itu.
        </p>
      </div>
    </FormModal>
  );
};

export default AssignHubModal;
